import { Fragment, useEffect, useId, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { Button } from "@/components/Button";
import { Dialog, DialogFooter } from "@/components/Dialog";
import { Kbd } from "@/components/Kbd";
import { Tooltip } from "@/components/Tooltip";
import {
  EVERYWHERE,
  isMac,
  isTypingTarget,
  keyCap,
  SCREENS,
  screenAt,
  SHORTCUTS,
  type Chord,
  type ScreenKey,
  type ShortcutGroup,
} from "@/lib/shortcuts";

import styles from "./Help.module.css";
import { useWhatsNew, WhatsNewSection, type WhatsNew } from "./WhatsNewNote";

/**
 * The review's own take-over button — how the schema step tells the review
 * from what else stands at its address (the form choice, a reading in work).
 * Read off the page: the review screen is not changed while the M2
 * measurements run, so it cannot say so itself.
 */
const REVIEW_ON_SCREEN = '[data-role="take-over"]';

/** The screen in front of the person, read when the overview opens. */
function screenHere(pathname: string): { screen: ScreenKey | null } {
  return { screen: screenAt(pathname, document.querySelector(REVIEW_ON_SCREEN) !== null) };
}

/** One way to press it: caps joined by „+", a set of keys (the arrows) side by side. */
function ChordKeys({ chord, mac }: { chord: Chord; mac: boolean }) {
  return (
    <span className={styles.chord}>
      {chord.map((part, index) => (
        <Fragment key={index}>
          {index > 0 ? <span className={styles.plus}>+</span> : null}
          {typeof part === "string" ? (
            <Kbd>{keyCap(part, mac)}</Kbd>
          ) : (
            <span className={styles.set}>
              {part.map((key) => (
                <Kbd key={key}>{keyCap(key, mac)}</Kbd>
              ))}
            </span>
          )}
        </Fragment>
      ))}
    </span>
  );
}

function KeyGroup({ group, mac }: { group: ShortcutGroup; mac: boolean }) {
  return (
    <section className={styles.group}>
      <h3 className={`label ${styles.groupTitle}`}>{group.title}</h3>
      {group.note !== undefined ? <p className={styles.note}>{group.note}</p> : null}
      <dl className={styles.rows}>
        {group.ids.map((id) => {
          const shortcut = SHORTCUTS[id];
          return (
            <div key={id} className={styles.row}>
              <dt className={styles.keys}>
                {shortcut.chords.map((chord, index) => (
                  <Fragment key={index}>
                    {index > 0 ? <span className={styles.or}>oder</span> : null}
                    <ChordKeys chord={chord} mac={mac} />
                  </Fragment>
                ))}
              </dt>
              <dd className={styles.does}>{shortcut.does}</dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}

/**
 * The keys of the screen in front of the person, then the ones that work
 * everywhere, then — folded — what is new in this version. One dialog, the
 * shell's own (UX ruling 8): Escape closes it, the focus goes back to
 * whatever opened it.
 *
 * What is new and not yet put away stands first (UX-Gutachten 2, B-5): the
 * masthead's „?" carries a dot for it, and this is where the dot leads. Put
 * away, it goes to its folded place at the end, and the focus to the list.
 *
 * The focus starts on the list's top (`data-autofocus` on the list, which is
 * not in the Tab order): on the board the list is long, and the first
 * control is the button at its end — the box would open scrolled down.
 */
function HelpDialog({
  screen,
  whatsNew,
  onClose,
}: {
  screen: ScreenKey | null;
  whatsNew: WhatsNew;
  onClose: () => void;
}) {
  const mac = isMac();
  const help = screen !== null ? SCREENS[screen] : null;
  const { news, waiting } = whatsNew;
  const listRef = useRef<HTMLDivElement>(null);
  return (
    <Dialog
      title="Tastenkürzel"
      subtitle={help !== null ? `Auf diesem Schirm: ${help.name}` : undefined}
      onClose={onClose}
    >
      <div ref={listRef} className={styles.list} tabIndex={-1} data-autofocus>
        {news !== null && waiting ? (
          <WhatsNewSection
            news={news}
            onPutAway={() => {
              whatsNew.putAway();
              listRef.current?.focus();
            }}
          />
        ) : null}
        {help === null ? (
          <p className={styles.none}>Für diesen Schirm sind keine eigenen Kürzel aufgeführt.</p>
        ) : help.groups.length === 0 ? (
          <p className={styles.none}>Dieser Schirm hat keine eigenen Kürzel.</p>
        ) : (
          help.groups.map((group) => <KeyGroup key={group.title} group={group} mac={mac} />)
        )}
        <KeyGroup group={EVERYWHERE} mac={mac} />
        {news !== null && !waiting ? (
          <details className={styles.news}>
            <summary className={styles.newsSummary}>Neu in dieser Version</summary>
            <ul className={styles.newsItems}>
              {news.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </details>
        ) : null}
      </div>
      <DialogFooter>
        <Button variant="tinte" onClick={onClose}>
          Schließen
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

/**
 * Help in the app (Welle 1, idea 16): „?" — Shift+ß on a German keyboard —
 * opens the overview of the keys this screen answers, and the masthead
 * carries a way to it for whoever does not know the key yet. Shortcuts
 * nobody can see are shortcuts nobody uses.
 *
 * Since UX-Gutachten 2 (B-5) that way is a round key „?" of 32 px with the
 * tooltip „Tastenkürzel (?)", in the one type rule of the masthead's right
 * end — the word „Tastenkürzel" and a key cap beside it were two of five
 * faces there. While something new waits for the person, the key carries a
 * dot (`useWhatsNew`), and the overview opens with it on top.
 *
 * „?" is a character before it is a shortcut: a field somebody types in
 * keeps it, and so does a press with Strg, ⌘ or Alt, a held key's repeats,
 * or one a handler further in already answered. Another dialog owns the
 * keyboard while it is open.
 *
 * While this one is open, it owns the keyboard. The screens answer their
 * keys on `document` — the review takes over on Strg+Enter and takes back a
 * number on Strg+Z, the boards maximize on F — and a key pressed to try
 * what the list names would do it unseen behind the box. So every key stops
 * at the window (its default stays: Enter still presses the focused button,
 * Space still opens the disclosure), Escape and a second „?" close the box
 * and nothing else, and Tab goes on to the dialog's own trap.
 *
 * Which screen is read when the overview opens, not before: the schema
 * step's address shows the review only once a reading has one to show. And
 * the box closes when the address changes under it — the browser's back
 * would leave it naming a screen that is gone.
 */
export function HelpControl() {
  const { pathname } = useLocation();
  const whatsNew = useWhatsNew();
  const newsHint = useId();
  const [shown, setShown] = useState<{ screen: ScreenKey | null } | null>(null);
  const [shownAt, setShownAt] = useState(pathname);
  if (shownAt !== pathname) {
    setShownAt(pathname);
    setShown(null);
  }

  useEffect(() => {
    if (shown !== null) return;
    function open(event: KeyboardEvent) {
      if (event.key !== "?" || event.repeat || event.defaultPrevented) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]') !== null) return;
      event.preventDefault();
      setShown(screenHere(pathname));
    }
    document.addEventListener("keydown", open);
    return () => document.removeEventListener("keydown", open);
  }, [shown, pathname]);

  useEffect(() => {
    if (shown === null) return;
    function hold(event: KeyboardEvent) {
      if (event.key === "Tab") return;
      event.stopPropagation();
      if (event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key !== "Escape" && event.key !== "?") return;
      event.preventDefault();
      setShown(null);
    }
    window.addEventListener("keydown", hold, true);
    return () => window.removeEventListener("keydown", hold, true);
  }, [shown]);

  return (
    <>
      <Tooltip text="Tastenkürzel (?)">
        <button
          type="button"
          className={styles.trigger}
          aria-label="Tastenkürzel (?)"
          aria-haspopup="dialog"
          aria-keyshortcuts="?"
          aria-describedby={whatsNew.waiting ? newsHint : undefined}
          data-neu={whatsNew.waiting ? "" : undefined}
          onClick={() => setShown(screenHere(pathname))}
        >
          {/* „?" is the visible label, and stands in the name too (WCAG
              2.5.3, fix round 1 of Task 15): „Tastenkürzel (?)", as the
              tooltip says it. */}
          <span aria-hidden="true">?</span>
          {/* The dot's colour is the masthead's (AppShell.module.css): this
              file names none. */}
          {whatsNew.waiting ? <span className={styles.dot} data-neu-punkt="" aria-hidden="true" /> : null}
        </button>
      </Tooltip>
      {whatsNew.waiting ? (
        <span id={newsHint} hidden>
          Neu in dieser Version
        </span>
      ) : null}
      {shown !== null ? (
        <HelpDialog screen={shown.screen} whatsNew={whatsNew} onClose={() => setShown(null)} />
      ) : null}
    </>
  );
}
